import { SITE_LANG } from "@/constants/locale";

const CBG_COMP_PACK_SRC =
  "https://compliance-pack.carlsberggroup.com/cbgCompPack.js";

const CBG_COMP_PACK_POLICIES_BASE_URL =
  "https://compliance.carlsberggroup.com";

/** Ottilia / Carlsberg Compliance Plugin defaults; override per environment if needed. */
export const CBG_COMP_PACK_GTM_ID = "GTM-MRQHKNDD";
export const CBG_COMP_PACK_PROJECT_ID =
  "b4c551b4-9ee5-4818-bc92-48b25e55005d";
export const CBG_COMP_PACK_MARKET = "se";
export const CBG_COMP_PACK_VERIFY_AGE = false;
export const CBG_COMP_PACK_COOKIES_PAGE = "/cookie-policy";
export const CBG_COMP_PACK_POLICY_PAGE = "/privacy-policy";
/** Fallback URL when the compliance pack is not ready yet. */
export const CBG_COMP_PACK_MANAGE_COOKIES_HREF = "/cookie-policy?cbgManage=true";
export const CBG_COMP_PACK_MANAGE_COOKIES_BUTTON_LABEL = "Hantera cookieinställningar";

export type CbgCompPackConfig = {
  gtmId: string;
  projectId: string;
  market: string;
  verifyAge: boolean;
  cookiesPage: string;
  policyPage: string;
  src: string;
};

export type CbgCompPackPolicyUrls = {
  cookiePolicyUrl: string;
  privacyPolicyUrl: string;
};

function parseVerifyAge(value: string | undefined): boolean | undefined {
  if (value === undefined) return undefined;
  const normalized = value.trim().toLowerCase();
  if (normalized === "true") return true;
  if (normalized === "false") return false;
  return undefined;
}

function resolvePolicyLang(): string {
  return (
    process.env.NEXT_PUBLIC_CBG_COMP_PACK_POLICY_LANG?.trim() || SITE_LANG
  );
}

export function getCbgCompPackExternalPolicyUrls(
  config?: Pick<CbgCompPackConfig, "projectId" | "market">,
): CbgCompPackPolicyUrls | null {
  const projectId =
    config?.projectId ??
    process.env.NEXT_PUBLIC_CBG_COMP_PACK_PROJECT_ID?.trim() ??
    CBG_COMP_PACK_PROJECT_ID;
  const market =
    config?.market ??
    process.env.NEXT_PUBLIC_CBG_COMP_PACK_MARKET?.trim() ??
    CBG_COMP_PACK_MARKET;
  const policiesBaseUrl =
    process.env.NEXT_PUBLIC_CBG_COMP_PACK_POLICIES_BASE_URL?.trim() ||
    CBG_COMP_PACK_POLICIES_BASE_URL;

  if (!projectId || !market) return null;

  const localeKey = `${resolvePolicyLang()}-${market}`;

  return {
    privacyPolicyUrl: `${policiesBaseUrl}/externals/${localeKey}?id=${projectId}`,
    cookiePolicyUrl: `${policiesBaseUrl}/externals/${localeKey}/cookies?id=${projectId}`,
  };
}

export function getCbgCompPackConfig(): CbgCompPackConfig | null {
  const gtmId =
    process.env.NEXT_PUBLIC_CBG_COMP_PACK_GTM_ID?.trim() || CBG_COMP_PACK_GTM_ID;
  const projectId =
    process.env.NEXT_PUBLIC_CBG_COMP_PACK_PROJECT_ID?.trim() ||
    CBG_COMP_PACK_PROJECT_ID;
  const market =
    process.env.NEXT_PUBLIC_CBG_COMP_PACK_MARKET?.trim() || CBG_COMP_PACK_MARKET;
  const verifyAge =
    parseVerifyAge(process.env.NEXT_PUBLIC_CBG_COMP_PACK_VERIFY_AGE) ??
    CBG_COMP_PACK_VERIFY_AGE;
  const cookiesPage =
    process.env.NEXT_PUBLIC_CBG_COMP_PACK_COOKIES_PAGE?.trim() ||
    CBG_COMP_PACK_COOKIES_PAGE;
  const policyPage =
    process.env.NEXT_PUBLIC_CBG_COMP_PACK_POLICY_PAGE?.trim() ||
    CBG_COMP_PACK_POLICY_PAGE;

  if (!gtmId || !projectId || !market) return null;

  return {
    gtmId,
    projectId,
    market,
    verifyAge,
    cookiesPage,
    policyPage,
    src: CBG_COMP_PACK_SRC,
  };
}
